import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import {
  Address,
  AddressProducts,
  AddressProductsSelectedEvent,
  ContractStatusEnum,
  createAddress,
  NavigatingBackTo,
  Product,
} from '@mwe/models';
import { LoadingService, productStore } from '@mwe/services';
import { getAddressGroupFormatted, getProductCategories, isArrayWithMinOneItem, isArrayWithOneItem } from '@mwe/utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { PreLoadDataComponent } from '../../pre-load-data/pre-load-data.component';
import { BasicCardClickableComponent } from '../../cards/card-clickable/basic-card-clickable.component';
import { NoAssociationsFoundWarningComponent } from '../../no-associations-found-warning/no-associations-found-warning.component';

@Component({
  selector: 'mwe-address-selection',
  templateUrl: './address-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PreLoadDataComponent, BasicCardClickableComponent, NoAssociationsFoundWarningComponent],
})
export class AddressSelectionComponent {
  noAddressGroupsTitle = input<string>();
  noAddressGroupsText = input<string>();
  extendedLoadingSpinner = input<boolean>(false);
  navigatingBackTo = input<NavigatingBackTo>();
  productsFilterFunc = input<(p: Product[]) => boolean>();

  productSearchBtnClicked = output();
  addressSelected = output<AddressProductsSelectedEvent>();
  hasNoAddressProducts = output();
  getProductCategories = getProductCategories;
  private productStore = inject(productStore);
  addressOptions = computed<AddressProducts[]>(() => {
    if (this.productStore.hasInactiveProducts() || this.productStore.currentState() === ContractStatusEnum.Beendet) return [];
    const addressGroups = this.productStore.addressGroups();
    if (!this.productsFilterFunc()) {
      return addressGroups;
    }
    return addressGroups.filter(aO => this.productsFilterFunc()(aO?.products));
  });
  hasNoAddressOption = computed<boolean>(() => !this.addressOptions()?.length);
  private loadingService = inject(LoadingService);
  state = toSignal(this.loadingService.getLatestMergedLoadingStates('addressProducts'));

  protected readonly getAddressGroupFormatted = getAddressGroupFormatted;

  constructor() {
    effect(() => {
      if (!isArrayWithMinOneItem(this.addressOptions())) {
        this.hasNoAddressProducts.emit();
      }
      if (isArrayWithOneItem(this.addressOptions())) {
        this.skipStepForOnlyOneAddress();
      }
    });
  }

  selectOption(address: Address, products: Product[]): void {
    this.addressSelected.emit({
      addressProduct: { address, products },
      selectionWasSkippedDueToOneAddress: false,
    });
  }

  private skipStepForOnlyOneAddress() {
    if (!isArrayWithOneItem(this.addressOptions())) return;

    const address = createAddress(this.addressOptions()[0].address);
    const products = this.addressOptions()[0].products;

    this.addressSelected.emit({
      addressProduct: { address, products },
      selectionWasSkippedDueToOneAddress: true,
    });
  }
}
